import React, { useState, useEffect, useCallback } from 'react';
import {
  FiSave,
  FiBell,
  FiShield,
  FiMail,
  FiGlobe
} from 'react-icons/fi';
import { api } from '../../services/api';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';

function LinkToggleField({ label, enabled, url, onEnabledChange, onUrlChange, placeholder }) {
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-4 space-y-3">
      <div className="flex items-center justify-between gap-4">
        <label className="text-sm font-semibold text-gray-300">{label}</label>
        <label className="relative inline-flex items-center cursor-pointer shrink-0">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600" />
          <span className="ms-2 text-xs text-gray-400">{enabled ? 'Affiché' : 'Masqué'}</span>
        </label>
      </div>
      <input
        type="url"
        value={url}
        onChange={(e) => onUrlChange(e.target.value)}
        placeholder={placeholder}
        disabled={!enabled}
        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all disabled:opacity-50"
      />
    </div>
  );
}

function mapConfigToState(data) {
  const app = data.app || {};
  const notif = data.notifications || {};
  const sec = data.security || {};
  const em = data.email || {};
  const landing = data.landing || {};
  const stores = landing.stores || {};
  const social = landing.social || {};
  return {
    siteName: app.name ?? 'Liberty',
    siteEmail: app.contactEmail ?? 'contact@liberty.com',
    sitePhone: app.contactPhone ?? '',
    demenageurSearchRadiusKm: app.demenageurSearchRadiusKm ?? 20,
    maintenanceMode: !!app.maintenance,
    emailNotifications: notif.emailEnabled !== false,
    smsNotifications: !!notif.smsEnabled,
    defaultLanguage: app.defaultLanguage || 'fr',
    timezone: app.timezone || 'Europe/Paris',
    sessionMinutes: Math.max(5, Math.round((sec.sessionTimeout || 3600) / 60)),
    maxLoginAttempts: sec.maxLoginAttempts ?? 5,
    phoneVerificationRequired: app.phoneVerificationRequired !== false,
    smtpHost: em.smtpHost || '',
    smtpPort: em.smtpPort != null ? String(em.smtpPort) : '587',
    smtpFrom: em.fromAddress || '',
    appStoreUrl: stores.appStore?.url ?? '',
    appStoreEnabled: stores.appStore?.enabled !== false,
    googlePlayUrl: stores.googlePlay?.url ?? '',
    googlePlayEnabled: stores.googlePlay?.enabled !== false,
    socialFacebookUrl: social.facebook?.url ?? '',
    socialFacebookEnabled: social.facebook?.enabled !== false,
    socialInstagramUrl: social.instagram?.url ?? '',
    socialInstagramEnabled: social.instagram?.enabled !== false,
    socialTiktokUrl: social.tiktok?.url ?? '',
    socialTiktokEnabled: social.tiktok?.enabled !== false,
  };
}

function buildPatchFromState(s) {
  const minutes = parseInt(s.sessionMinutes, 10);
  const sessionMinutes = Number.isFinite(minutes) && minutes > 0 ? minutes : 60;
  return {
    app: {
      name: (s.siteName || '').trim() || 'Liberty',
      contactEmail: (s.siteEmail || '').trim() || 'contact@liberty.com',
      contactPhone: (s.sitePhone || '').trim(),
      demenageurSearchRadiusKm: Math.min(
        200,
        Math.max(1, parseInt(s.demenageurSearchRadiusKm, 10) || 20)
      ),
      defaultLanguage: s.defaultLanguage || 'fr',
      timezone: s.timezone || 'Europe/Paris',
      maintenance: s.maintenanceMode,
      phoneVerificationRequired: s.phoneVerificationRequired !== false,
    },
    notifications: {
      emailEnabled: s.emailNotifications,
      smsEnabled: s.smsNotifications,
    },
    security: {
      sessionTimeout: Math.max(300, sessionMinutes * 60),
      maxLoginAttempts: Math.max(1, parseInt(s.maxLoginAttempts, 10) || 5),
    },
    email: {
      smtpHost: (s.smtpHost || '').trim(),
      smtpPort: Math.min(65535, Math.max(1, parseInt(s.smtpPort, 10) || 587)),
      fromAddress: (s.smtpFrom || '').trim(),
    },
    landing: {
      stores: {
        appStore: { url: (s.appStoreUrl || '').trim(), enabled: !!s.appStoreEnabled },
        googlePlay: { url: (s.googlePlayUrl || '').trim(), enabled: !!s.googlePlayEnabled },
      },
      social: {
        facebook: { url: (s.socialFacebookUrl || '').trim(), enabled: !!s.socialFacebookEnabled },
        instagram: { url: (s.socialInstagramUrl || '').trim(), enabled: !!s.socialInstagramEnabled },
        tiktok: { url: (s.socialTiktokUrl || '').trim(), enabled: !!s.socialTiktokEnabled },
      },
    },
  };
}

const initialForm = () =>
  mapConfigToState({
    app: {},
    notifications: {},
    security: {},
    email: {},
    landing: {},
  });

function AdminSettings() {
  const { refresh: refreshPublicSettings } = useSystemSettings();
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoadError(null);
      setInitialLoading(true);
      const response = await api.getSystemSettings();
      if (response?.success && response.data) {
        setSettings(mapConfigToState(response.data));
      } else {
        throw new Error(response?.message || 'Chargement impossible');
      }
    } catch (err) {
      setLoadError(err.message || 'Erreur de chargement');
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const patch = buildPatchFromState(settings);
      const response = await api.updateSystemSettings(patch);
      if (response?.success && response.data) {
        setSettings(mapConfigToState(response.data));
        refreshPublicSettings();
        alert('Paramètres sauvegardés avec succès!');
      } else {
        throw new Error(response?.message || 'Échec de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert(error.message || 'Erreur lors de la sauvegarde des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'Général', icon: <FiGlobe className="w-5 h-5" />, gradient: 'from-blue-500 to-cyan-600' },
    { id: 'notifications', label: 'Notifications', icon: <FiBell className="w-5 h-5" />, gradient: 'from-purple-500 to-pink-600' },
    { id: 'security', label: 'Sécurité', icon: <FiShield className="w-5 h-5" />, gradient: 'from-red-500 to-rose-600' },
    { id: 'email', label: 'Email', icon: <FiMail className="w-5 h-5" />, gradient: 'from-indigo-500 to-purple-600' }
  ];

  const activeTabData = tabs.find((tab) => tab.id === activeTab);

  if (initialLoading) {
    return (
      <div className="p-12 text-center text-gray-400">
        Chargement des paramètres...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-12 text-center space-y-4">
        <p className="text-red-400">{loadError}</p>
        <button
          type="button"
          onClick={loadSettings}
          className="px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg font-semibold"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-yellow-400 mb-2">
          Paramètres
        </h1>
        <p className="text-gray-400 font-medium">Configurez les paramètres de la plateforme (enregistrés en base)</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-4 py-3.5 text-sm font-semibold rounded-lg transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/50 shadow-lg'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-yellow-400 border border-transparent'
                  }`}
                >
                  <span className="mr-3">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-8">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div className="flex items-center mb-6">
                  <div className="p-3 rounded-lg bg-yellow-600/20 border border-yellow-600/50 mr-4">
                    <FiGlobe className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Paramètres généraux</h2>
                    <p className="text-sm text-gray-400 mt-1">Configuration de base de la plateforme</p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Nom du site
                    </label>
                    <input
                      type="text"
                      value={settings.siteName}
                      onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Email de contact
                    </label>
                    <input
                      type="email"
                      value={settings.siteEmail}
                      onChange={(e) => setSettings({ ...settings, siteEmail: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
                    />
                    <p className="mt-1 text-xs text-gray-500">Affiché dans la section Contact du site public</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Téléphone de contact
                    </label>
                    <input
                      type="tel"
                      value={settings.sitePhone}
                      onChange={(e) => setSettings({ ...settings, sitePhone: e.target.value })}
                      placeholder="+216 XX XXX XXX"
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
                    />
                    <p className="mt-1 text-xs text-gray-500">Affiché dans la section Contact du site public</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Rayon de recherche des déménageurs (km)
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={200}
                      step={1}
                      value={settings.demenageurSearchRadiusKm}
                      onChange={(e) =>
                        setSettings({ ...settings, demenageurSearchRadiusKm: e.target.value })
                      }
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Distance maximale affichée sur l&apos;écran d&apos;accueil client (application mobile)
                    </p>
                  </div>

                  <div className="border-t border-gray-700 pt-6 space-y-4">
                    <div>
                      <h3 className="text-lg font-bold text-yellow-400">Boutons App Store / Google Play</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Liens et visibilité sur la page d&apos;accueil (section téléchargement)
                      </p>
                    </div>
                    <LinkToggleField
                      label="App Store"
                      enabled={settings.appStoreEnabled}
                      url={settings.appStoreUrl}
                      onEnabledChange={(v) => setSettings({ ...settings, appStoreEnabled: v })}
                      onUrlChange={(v) => setSettings({ ...settings, appStoreUrl: v })}
                      placeholder="https://apps.apple.com/app/..."
                    />
                    <LinkToggleField
                      label="Google Play"
                      enabled={settings.googlePlayEnabled}
                      url={settings.googlePlayUrl}
                      onEnabledChange={(v) => setSettings({ ...settings, googlePlayEnabled: v })}
                      onUrlChange={(v) => setSettings({ ...settings, googlePlayUrl: v })}
                      placeholder="https://play.google.com/store/apps/details?id=..."
                    />
                  </div>

                  <div className="border-t border-gray-700 pt-6 space-y-4">
                    <div>
                      <h3 className="text-lg font-bold text-yellow-400">Réseaux sociaux</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Liens et visibilité dans le pied de page du site public
                      </p>
                    </div>
                    <LinkToggleField
                      label="Facebook"
                      enabled={settings.socialFacebookEnabled}
                      url={settings.socialFacebookUrl}
                      onEnabledChange={(v) => setSettings({ ...settings, socialFacebookEnabled: v })}
                      onUrlChange={(v) => setSettings({ ...settings, socialFacebookUrl: v })}
                      placeholder="https://facebook.com/..."
                    />
                    <LinkToggleField
                      label="Instagram"
                      enabled={settings.socialInstagramEnabled}
                      url={settings.socialInstagramUrl}
                      onEnabledChange={(v) => setSettings({ ...settings, socialInstagramEnabled: v })}
                      onUrlChange={(v) => setSettings({ ...settings, socialInstagramUrl: v })}
                      placeholder="https://instagram.com/..."
                    />
                    <LinkToggleField
                      label="TikTok"
                      enabled={settings.socialTiktokEnabled}
                      url={settings.socialTiktokUrl}
                      onEnabledChange={(v) => setSettings({ ...settings, socialTiktokEnabled: v })}
                      onUrlChange={(v) => setSettings({ ...settings, socialTiktokUrl: v })}
                      placeholder="https://tiktok.com/@..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Langue par défaut
                    </label>
                    <select
                      value={settings.defaultLanguage}
                      onChange={(e) => setSettings({ ...settings, defaultLanguage: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    >
                      <option value="fr">Français</option>
                      <option value="en">English</option>
                      <option value="ar">العربية</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Fuseau horaire
                    </label>
                    <select
                      value={settings.timezone}
                      onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    >
                      <option value="Europe/Paris">Europe/Paris (GMT+1)</option>
                      <option value="Africa/Tunis">Africa/Tunis</option>
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">America/New_York (GMT-5)</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200/60">
                    <div>
                      <label className="block text-sm font-semibold text-white mb-1">
                        Mode maintenance
                      </label>
                      <p className="text-xs text-gray-400">Désactive l&apos;accès public à la plateforme</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.maintenanceMode}
                        onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-yellow-600" />
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="flex items-center mb-6">
                  <div className={`p-3 rounded-xl bg-gradient-to-r ${activeTabData?.gradient} mr-4`}>
                    <FiBell className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Paramètres de notifications</h2>
                    <p className="text-sm text-gray-400 mt-1">Configurez les préférences de notification</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200/60">
                    <div>
                      <label className="block text-sm font-semibold text-white mb-1">
                        Notifications par email
                      </label>
                      <p className="text-xs text-gray-400">Recevoir des notifications par email</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications}
                        onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-yellow-600" />
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200/60">
                    <div>
                      <label className="block text-sm font-semibold text-white mb-1">
                        Notifications par SMS
                      </label>
                      <p className="text-xs text-gray-400">Recevoir des notifications par SMS</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.smsNotifications}
                        onChange={(e) => setSettings({ ...settings, smsNotifications: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-yellow-600" />
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-6">
                <div className="flex items-center mb-6">
                  <div className={`p-3 rounded-xl bg-gradient-to-r ${activeTabData?.gradient} mr-4`}>
                    <FiShield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Paramètres de sécurité</h2>
                    <p className="text-sm text-gray-400 mt-1">Durée de session et tentatives de connexion (enregistrées en base)</p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div className="p-5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60 rounded-xl">
                    <div className="flex items-start">
                      <FiShield className="text-amber-600 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h3 className="text-sm font-bold text-amber-900 mb-1">Sécurité renforcée</h3>
                        <p className="text-sm text-amber-800">
                          La 2FA globale et d&apos;autres options avancées sont dans la page
                          {' '}
                          <span className="font-semibold">Configuration</span>
                          .
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Durée de session (minutes)
                    </label>
                    <input
                      type="number"
                      min={5}
                      value={settings.sessionMinutes}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          sessionMinutes: Math.max(5, parseInt(e.target.value, 10) || 60),
                        })
                      }
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Tentatives de connexion max
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={settings.maxLoginAttempts}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          maxLoginAttempts: Math.max(1, parseInt(e.target.value, 10) || 5),
                        })
                      }
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    />
                  </div>

                  <div className="flex items-center justify-between p-5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200/60">
                    <div>
                      <label className="block text-sm font-semibold text-white mb-1">
                        Vérification du numéro de téléphone
                      </label>
                      <p className="text-xs text-gray-400">
                        Si activé, un code SMS (OTP) est exigé à l&apos;inscription client et déménageur.
                        Désactivez pour créer les comptes sans SMS.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.phoneVerificationRequired !== false}
                        onChange={(e) =>
                          setSettings({ ...settings, phoneVerificationRequired: e.target.checked })
                        }
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-yellow-600" />
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'email' && (
              <div className="space-y-6">
                <div className="flex items-center mb-6">
                  <div className={`p-3 rounded-xl bg-gradient-to-r ${activeTabData?.gradient} mr-4`}>
                    <FiMail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Configuration email</h2>
                    <p className="text-sm text-gray-400 mt-1">
                      Préférences SMTP enregistrées en base — l&apos;envoi peut encore utiliser les variables d&apos;environnement du serveur
                    </p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Serveur SMTP
                    </label>
                    <input
                      type="text"
                      placeholder="smtp.example.com"
                      value={settings.smtpHost}
                      onChange={(e) => setSettings({ ...settings, smtpHost: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Port SMTP
                    </label>
                    <input
                      type="number"
                      placeholder="587"
                      value={settings.smtpPort}
                      onChange={(e) => setSettings({ ...settings, smtpPort: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Email d&apos;envoi
                    </label>
                    <input
                      type="email"
                      placeholder="noreply@liberty.com"
                      value={settings.smtpFrom}
                      onChange={(e) => setSettings({ ...settings, smtpFrom: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-yellow-600/30">
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="px-8 py-3.5 bg-yellow-600 text-gray-900 rounded-xl hover:bg-yellow-500 transition-all duration-300 flex items-center font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiSave className="mr-2" />
                {loading ? 'Sauvegarde...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminSettings;
